import React, { useState, useEffect } from 'react';
import { Instagram, Facebook, CheckCircle, Link as LinkIcon, Trash2, Share2, Clock, Image, Car, TrendingDown, Settings, Wand2, Palette } from 'lucide-react';
import api from '../../../services/api';
import { SettingsSection, SettingsToggle, SettingsSelect } from '../index';
import TabTemplates from './TabTemplates';
import TabAI from './TabAI';
import {
  PUBLICATION_CHANNELS,
  PUBLICATION_EVENT_TYPES,
  DEFAULT_PUBLICATION_EVENTS,
  WATERMARK_POSITIONS,
  DELAY_OPTIONS,
  MAX_PUBLICATIONS_OPTIONS,
} from '../../../constants/settings';

const EVENT_ICONS = { Car, TrendingDown, CheckCircle };

const SUB_TABS = [
  { id: 'config', label: 'Comptes & Publication', icon: Settings },
  { id: 'ai', label: 'Contenu IA', icon: Wand2 },
  { id: 'templates', label: 'Templates', icon: Palette },
];

const TabReseauxSociaux = ({ settings, onChange, initialSubTab }) => {
  const [activeSubTab, setActiveSubTab] = useState(
    initialSubTab === 'templates' ? 'templates' : initialSubTab === 'ai' ? 'ai' : 'config'
  );
  const [accounts, setAccounts] = useState([]);
  const [connecting, setConnecting] = useState(false);
  const [oauthError, setOauthError] = useState('');
  const [oauthSuccess, setOauthSuccess] = useState('');

  useEffect(() => {
    loadAccounts();
    // Check for OAuth callback results in URL
    const params = new URLSearchParams(window.location.search);
    const success = params.get('success');
    const error = params.get('error');
    const platforms = params.get('platforms');
    if (success === 'true' && platforms) {
      setOauthSuccess(`Compte(s) connecté(s) : ${platforms.replace(',', ', ')}`);
    } else if (error) {
      setOauthError(error);
    }
  }, []);

  const loadAccounts = async () => {
    try {
      const res = await api.get('/oauth/accounts');
      setAccounts(res.data.accounts || []);
    } catch (err) {
      // silent
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    setOauthError('');
    try {
      const res = await api.get('/oauth/connect');
      window.location.href = res.data.auth_url;
    } catch (err) {
      setOauthError(err.response?.data?.detail || 'Erreur lors de la connexion');
      setConnecting(false);
    }
  };

  const handleDisconnect = async (platform) => {
    if (!window.confirm(`Déconnecter votre compte ${platform} ?`)) return;
    try {
      await api.delete(`/oauth/disconnect/${platform}`);
      setAccounts(accounts.filter(a => a.platform !== platform));
      setOauthSuccess(`Compte ${platform} déconnecté`);
    } catch (err) {
      setOauthError('Erreur lors de la déconnexion');
    }
  };

  const getAccount = (platform) => accounts.find(a => a.platform === platform);
  const instagramAccount = getAccount('instagram');
  const facebookAccount = getAccount('facebook');

  const updateSetting = (key, value) => {
    onChange({ ...settings, [key]: value });
  };

  const getPublicationEvents = () => settings?.publication_events || DEFAULT_PUBLICATION_EVENTS;

  const toggleEventChannel = (eventId, channelId) => {
    const current = getPublicationEvents();
    const eventConfig = current[eventId] || {};
    onChange({
      ...settings,
      publication_events: {
        ...current,
        [eventId]: { ...eventConfig, [channelId]: !eventConfig[channelId] },
      },
    });
  };

  const pubEvents = getPublicationEvents();

  return (
    <div className="space-y-6">
      {/* Sub-tab navigation */}
      <div className="flex gap-1 bg-dark-hover rounded-lg p-1">
        {SUB_TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveSubTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${
                activeSubTab === tab.id
                  ? 'bg-dark-card text-primary shadow-sm'
                  : 'text-secondary hover:text-primary'
              }`}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Templates sub-tab */}
      {activeSubTab === 'templates' && (
        <TabTemplates settings={settings} onChange={onChange} />
      )}

      {/* AI Content sub-tab */}
      {activeSubTab === 'ai' && (
        <TabAI settings={settings} onChange={onChange} />
      )}

      {/* Config sub-tab */}
      {activeSubTab === 'config' && (
      <div className="space-y-6">
      {/* OAuth Connections */}
      <SettingsSection title="Comptes connectés" icon={LinkIcon}>
        {oauthSuccess && (
          <div className="bg-green-900/20 text-green-400 p-3 rounded-md mb-4 text-sm flex items-center">
            <CheckCircle className="h-4 w-4 mr-2 flex-shrink-0" />
            {oauthSuccess}
          </div>
        )}
        {oauthError && (
          <div className="bg-red-900/20 text-red-400 p-3 rounded-md mb-4 text-sm">
            {oauthError}
          </div>
        )}

        <div className="space-y-3">
          {/* Instagram */}
          <div className="flex items-center justify-between p-3 border border-dark-border rounded-lg">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 p-2 rounded-lg">
                <Instagram className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-primary">Instagram</p>
                <p className="text-xs text-muted">
                  {instagramAccount
                    ? `@${instagramAccount.account_username || instagramAccount.account_id}`
                    : 'Non connecté'}
                </p>
              </div>
            </div>
            {instagramAccount ? (
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <button onClick={() => handleDisconnect('instagram')} className="text-red-400 hover:text-red-300 p-1">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <span className="text-xs text-muted">-</span>
            )}
          </div>

          {/* Facebook */}
          <div className="flex items-center justify-between p-3 border border-dark-border rounded-lg">
            <div className="flex items-center gap-3">
              <div className="bg-blue-600 p-2 rounded-lg">
                <Facebook className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-primary">Facebook</p>
                <p className="text-xs text-muted">
                  {facebookAccount
                    ? (facebookAccount.account_username || facebookAccount.account_id)
                    : 'Non connecté'}
                </p>
              </div>
            </div>
            {facebookAccount ? (
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <button onClick={() => handleDisconnect('facebook')} className="text-red-400 hover:text-red-300 p-1">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <span className="text-xs text-muted">-</span>
            )}
          </div>
        </div>

        {(!instagramAccount || !facebookAccount) && (
          <button
            onClick={handleConnect}
            disabled={connecting}
            className="mt-4 w-full inline-flex items-center justify-center px-4 py-2.5 bg-carmin text-white rounded-md hover:bg-carmin-light disabled:opacity-50 text-sm"
          >
            <LinkIcon className="h-4 w-4 mr-2" />
            {connecting ? 'Connexion...' : 'Connecter Instagram & Facebook'}
          </button>
        )}
        <p className="text-xs text-muted mt-3">
          OAuth Meta — votre mot de passe n'est jamais stocké.
        </p>
      </SettingsSection>

      {/* Event x Channel Matrix */}
      <SettingsSection title="Publications par événement" icon={Share2}>
        <p className="text-sm text-muted mb-4">
          Choisissez quels canaux sont actifs pour chaque type d'événement
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-dark-border">
                <th className="text-left py-3 pr-4 font-medium text-label">Événement</th>
                {PUBLICATION_CHANNELS.map(ch => (
                  <th key={ch.id} className="text-center py-3 px-3 font-medium text-label whitespace-nowrap">
                    <span className="text-xs">{ch.label}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PUBLICATION_EVENT_TYPES.map(event => {
                const IconComponent = EVENT_ICONS[event.icon];
                const eventConfig = pubEvents[event.id] || {};
                return (
                  <tr key={event.id} className="border-b border-dark-border last:border-0">
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-2">
                        {IconComponent && <IconComponent className="h-4 w-4 text-secondary" />}
                        <span className="text-label">{event.label}</span>
                      </div>
                    </td>
                    {PUBLICATION_CHANNELS.map(ch => (
                      <td key={ch.id} className="text-center py-3 px-3">
                        <button
                          type="button"
                          onClick={() => toggleEventChannel(event.id, ch.id)}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                            eventConfig[ch.id] ? 'bg-carmin' : 'bg-dark-hover'
                          }`}
                        >
                          <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                            eventConfig[ch.id] ? 'translate-x-5' : 'translate-x-0'
                          }`} />
                        </button>
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </SettingsSection>

      {/* Scheduling */}
      <SettingsSection title="Planification" icon={Clock}>
        <div className="space-y-6">
          <SettingsToggle
            label="Planification automatique"
            description="Activer la planification automatique des publications"
            checked={settings?.schedule_enabled || false}
            onChange={(value) => updateSetting('schedule_enabled', value)}
          />
          {settings?.schedule_enabled && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-label mb-2">Heure de début</label>
                  <div className="flex gap-2">
                    <select
                      value={settings?.schedule_start_hour ?? 8}
                      onChange={(e) => updateSetting('schedule_start_hour', parseInt(e.target.value))}
                      className="flex-1 bg-dark-input border border-dark-border text-primary rounded-md px-3 py-2"
                    >
                      {[...Array(24)].map((_, i) => (
                        <option key={i} value={i}>{i.toString().padStart(2, '0')}</option>
                      ))}
                    </select>
                    <span className="flex items-center text-secondary">:</span>
                    <select
                      value={settings?.schedule_start_minute ?? 30}
                      onChange={(e) => updateSetting('schedule_start_minute', parseInt(e.target.value))}
                      className="flex-1 bg-dark-input border border-dark-border text-primary rounded-md px-3 py-2"
                    >
                      {[0, 15, 30, 45].map((m) => (
                        <option key={m} value={m}>{m.toString().padStart(2, '0')}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-label mb-2">Heure de fin</label>
                  <div className="flex gap-2">
                    <select
                      value={settings?.schedule_end_hour ?? 20}
                      onChange={(e) => updateSetting('schedule_end_hour', parseInt(e.target.value))}
                      className="flex-1 bg-dark-input border border-dark-border text-primary rounded-md px-3 py-2"
                    >
                      {[...Array(24)].map((_, i) => (
                        <option key={i} value={i}>{i.toString().padStart(2, '0')}</option>
                      ))}
                    </select>
                    <span className="flex items-center text-secondary">:</span>
                    <select
                      value={settings?.schedule_end_minute ?? 30}
                      onChange={(e) => updateSetting('schedule_end_minute', parseInt(e.target.value))}
                      className="flex-1 bg-dark-input border border-dark-border text-primary rounded-md px-3 py-2"
                    >
                      {[0, 15, 30, 45].map((m) => (
                        <option key={m} value={m}>{m.toString().padStart(2, '0')}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-label mb-2">Maximum de publications par jour</label>
                <select
                  value={settings?.max_publications_per_day ?? 5}
                  onChange={(e) => updateSetting('max_publications_per_day', parseInt(e.target.value))}
                  className="w-full bg-dark-input border border-dark-border text-primary rounded-md px-3 py-2"
                >
                  {MAX_PUBLICATIONS_OPTIONS.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>

              <SettingsSelect
                label="Délai minimum entre publications"
                value={settings?.min_delay_minutes ?? 120}
                onChange={(value) => updateSetting('min_delay_minutes', parseInt(value))}
                options={DELAY_OPTIONS}
              />
            </>
          )}
        </div>
      </SettingsSection>

      {/* Watermark */}
      <SettingsSection title="Watermark" icon={Image}>
        <div className="space-y-6">
          {settings?.logo_url ? (
            <>
              <SettingsToggle
                label="Activer le watermark"
                description="Ajouter automatiquement votre logo sur les photos"
                checked={settings?.watermark_enabled || false}
                onChange={(value) => updateSetting('watermark_enabled', value)}
              />
              {settings?.watermark_enabled && (
                <>
                  <SettingsSelect
                    label="Position du watermark"
                    value={settings?.watermark_position || 'bottom-right'}
                    onChange={(value) => updateSetting('watermark_position', value)}
                    options={WATERMARK_POSITIONS}
                  />
                  <div>
                    <label className="block text-sm font-medium text-label mb-2">
                      Opacité: {Math.round((settings?.watermark_opacity || 0.8) * 100)}%
                    </label>
                    <input
                      type="range"
                      min="10"
                      max="100"
                      value={(settings?.watermark_opacity || 0.8) * 100}
                      onChange={(e) => updateSetting('watermark_opacity', parseInt(e.target.value) / 100)}
                      className="w-full accent-carmin"
                    />
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="p-4 bg-yellow-900/20 border border-yellow-500/30 rounded-lg">
              <p className="text-sm text-yellow-400">
                Uploadez d'abord un logo dans l'onglet "Mon entreprise" pour activer le watermark.
              </p>
            </div>
          )}
        </div>
      </SettingsSection>
      </div>
      )}
    </div>
  );
};

export default TabReseauxSociaux;
